import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, RotateCcw, Save, Trash2, ExternalLink, Upload } from "lucide-react";
import { BRAND_SLOTS } from "@/lib/brandAssets";

// Group slots by namespace for the UI.
const SLOT_GROUPS = {
  Logo: ["logo.horizontal.dark", "logo.horizontal.white", "logo.shape.purple"],
  Hero: ["hero.primary"],
  Sections: ["section.team.handshake", "section.team.board", "section.testimonials"],
  Decor: ["decor.hex.group", "decor.k.purple", "decor.o.glass"],
  Personas: [
    "persona.point-right",
    "persona.surprised-left",
    "persona.thumbs-up",
    "persona.cross-hand",
    "persona.coffee-laptop",
    "persona.professional",
  ],
};

const DESC = {
  "logo.horizontal.dark": "Navbar logo for light backgrounds (purple on dark text).",
  "logo.horizontal.white": "Footer logo for dark backgrounds (white + purple).",
  "logo.shape.purple": "Favicon, preloader and compact mark.",
  "hero.primary": "Main hero image on the landing page.",
  "section.team.handshake": "Team collaboration imagery (About / Team).",
  "section.team.board": "Company announcement / 'Join Team' imagery.",
  "section.testimonials": "Testimonial section primary graphic.",
  "decor.hex.group": "Floating hex group used in hero backdrop.",
  "decor.k.purple": "Floating K-shape decor.",
  "decor.o.glass": "Magnifying-glass O-shape decor.",
  "persona.point-right": "Girl pointing right (place left of CTA).",
  "persona.surprised-left": "Surprised girl pointing left (place right of feature).",
  "persona.thumbs-up": "Success / benefits illustration.",
  "persona.cross-hand": "Professional 'about' illustration.",
  "persona.coffee-laptop": "Contact / support illustration.",
  "persona.professional": "Generic professional placeholder.",
};

export default function AdminMediaManagerPage() {
  const [overrides, setOverrides] = useState({}); // slot_key -> {url, label, ...}
  const [drafts, setDrafts] = useState({}); // slot_key -> draft url being typed
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/media");
      const map = {};
      for (const row of r.data || []) {
        map[row.slot_key] = row;
      }
      setOverrides(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const draftFor = (key) =>
    drafts[key] !== undefined ? drafts[key] : (overrides[key]?.url ?? "");

  const save = async (key) => {
    const url = draftFor(key).trim();
    if (!url) { toast.error("Provide a URL or upload an image first"); return; }
    try {
      await api.put("/admin/media", { slot_key: key, url, label: key });
      toast.success("Media slot updated");
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const reset = async (key) => {
    if (!window.confirm("Revert this slot back to the default brand asset?")) return;
    try {
      await api.delete(`/admin/media/${encodeURIComponent(key)}`);
      toast.success("Reverted to default");
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      load();
    } catch (e) { toast.error("Reset failed"); }
  };

  const uploadFor = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    setDrafts((d) => ({ ...d, [key]: dataUrl }));
    toast.info("Image attached — click Save to apply");
  };

  return (
    <div data-testid="admin-media-manager-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MEDIA MANAGER</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Swap <em className="italic text-kb-purple">site images</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
            Replace any landing or section image without a code deploy. Paste a URL or upload an
            image (under 2 MB) — saved instantly. Reset a slot to revert to the default brand asset.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
      ) : (
        <div className="mt-8 space-y-10">
          {Object.entries(SLOT_GROUPS).map(([group, keys]) => (
            <section key={group} data-testid={`media-group-${group.toLowerCase()}`}>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple mb-3">{group}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {keys.map((key) => {
                  const live = overrides[key]?.url || BRAND_SLOTS[key];
                  const draft = draftFor(key);
                  const isOverridden = !!overrides[key];
                  const isDirty = drafts[key] !== undefined && drafts[key] !== (overrides[key]?.url ?? BRAND_SLOTS[key]);
                  return (
                    <div key={key} className="kb-card p-4 flex flex-col" data-testid={`media-slot-${key}`}>
                      <div className="aspect-[4/3] w-full bg-kb-subtle rounded-md overflow-hidden border border-kb-divider flex items-center justify-center">
                        {(draft || live) ? (
                          <img src={draft || live} alt={key} className="object-contain w-full h-full" />
                        ) : (
                          <ImageIcon className="w-10 h-10 text-kb-ink-light" />
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <code className="kb-mono text-[11px] text-kb-ink truncate">{key}</code>
                        {isOverridden && (
                          <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-purple bg-kb-subtle border border-kb-divider rounded-full px-1.5 py-0.5">CUSTOM</span>
                        )}
                      </div>
                      <p className="text-xs text-kb-ink-muted mt-1.5 line-clamp-2">{DESC[key]}</p>
                      <input
                        className="kb-input text-xs mt-3"
                        placeholder="Paste image URL or upload below"
                        value={draft}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                        data-testid={`media-slot-${key}-url`}
                      />
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <label className="kb-btn-outline text-xs cursor-pointer" data-testid={`media-slot-${key}-upload-label`}>
                          <Upload className="w-3 h-3" /> Upload
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => uploadFor(key, e.target.files?.[0])}
                            data-testid={`media-slot-${key}-upload-input`}
                          />
                        </label>
                        <button
                          onClick={() => save(key)}
                          disabled={!isDirty}
                          className="kb-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`media-slot-${key}-save`}
                        >
                          <Save className="w-3 h-3" /> Save
                        </button>
                        {isOverridden && (
                          <button
                            onClick={() => reset(key)}
                            className="kb-mono text-[10px] uppercase tracking-wider text-rose-600 inline-flex items-center gap-1 hover:underline"
                            data-testid={`media-slot-${key}-reset`}
                          >
                            <RotateCcw className="w-3 h-3" /> Reset
                          </button>
                        )}
                        {live && (
                          <a href={live} target="_blank" rel="noreferrer"
                             className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1 hover:text-kb-purple ml-auto">
                            <ExternalLink className="w-3 h-3" /> Open
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
